import React, { useEffect, useState } from "react";
import axios from "axios";

const SERVER_URL = "https://allinone-clone-7d5d795e98ce.herokuapp.com";

const AboutSection = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAbout = async () => {
      try {
        const res = await axios.get(`${SERVER_URL}/api/about-section`);
        if (res.data.success) {
          setData(res.data.data);
        }
      } catch (err) {
        console.error("Failed to load about section:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchAbout();
  }, []);

  if (loading) return null; // Or a loader
  if (!data || !data.isActive) return null;

  return (
    <>
      <section id="about" className="about-section position-relative overflow-hidden py-5">
        {/* Modern Dark Backgrounds with subtle ambient glows */}
        <div className="bg-absolute bg-dark-base"></div>
        <div className="ambient-glow glow-primary"></div>
        <div className="ambient-glow glow-secondary"></div>
        
        <div className="container-xl position-relative z-1 mt-5 pt-lg-5 pb-5 px-xl-5">
          <div className="row align-items-center g-5">
            
            {/* Left Column: Content & Stats */}
            <div className="col-lg-6 wow fadeInUp" data-wow-duration="1s">
              
              {data.subTitle && (
                <div className="badge-wrapper mb-4">
                  <span className="badge-premium d-inline-flex align-items-center px-3 py-2 rounded-pill border border-white-10 glass-panel tracking-wider text-uppercase shadow-sm">
                    <span className="badge-dot me-2"></span>
                    <span className="text-secondary fw-semibold text-xs">{data.subTitle}</span>
                  </span>
                </div>
              )}
              
              <h2 className="display-4 fw-bolder text-white mb-4 title-glow" style={{ letterSpacing: "-0.04em", lineHeight: "1.15" }}>
                {data.mainTitle}
              </h2>
              
              <p className="lead text-secondary mb-5 fs-5 pe-lg-4" style={{ lineHeight: "1.7" }}>
                {data.description}
              </p>

              {/* Stats Grid */}
              {data.stats && data.stats.length > 0 && (
                <div className="row g-4 mb-5">
                  {data.stats.map((stat, index) => (
                    <div key={stat.id || index} className="col-6 col-md-6">
                      <div className="stat-item position-relative ps-4 py-2">
                        <div className="position-absolute start-0 top-0 h-100 w-1 rounded" style={{ background: "linear-gradient(180deg, #6366f1, transparent)", width: "3px" }}></div>
                        <h3 className="fs-1 fw-bolder text-white mb-1 title-glow">{stat.value}</h3>
                        <p className="text-secondary small mb-0 fw-bold text-uppercase tracking-wider" style={{ letterSpacing: "0.1em" }}>
                          {stat.label}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Action Button */}
              {data.button && (
                <div className="mt-2">
                   <a href={data.button.link} className="btn btn-premium-action btn-lg px-5 py-3 rounded-pill fw-bold d-inline-flex align-items-center transition-all text-nowrap">
                     Explore Platform <i className="fa-solid fa-arrow-right ms-2 fs-6"></i>
                   </a>
                </div>
              )}

            </div>

            {/* Right Column: Massive Image/Feature Card */}
            <div className="col-lg-6 wow fadeInUp" data-wow-delay="0.2s" data-wow-duration="1s">
              <div className="position-relative">
                {/* Decorative background glow for image */}
                <div className="position-absolute top-50 start-50 translate-middle w-100 h-100 pointer-events-none" style={{ background: "radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, transparent 60%)", filter: "blur(60px)" }}></div>
                
                <div className="glass-panel p-2 rounded-4 border border-white-10 position-relative premium-card overflow-hidden shadow-premium">
                  <div className="position-relative z-1 rounded-4 overflow-hidden image-wrapper bg-dark-inner" style={{ aspectRatio: "4/5" }}>
                    <img
                      src={data.images?.main || "https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&q=80"}
                      alt="AI Content Generation"
                      className="img-fluid w-100 h-100 object-fit-cover mix-blend-overlay"
                      style={{ opacity: 0.85 }}
                    />
                    
                    {/* High-tech overlay grid */}
                    <div className="position-absolute top-0 start-0 w-100 h-100 pointer-events-none" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)", backgroundSize: "30px 30px" }}></div>
                    <div className="position-absolute bottom-0 start-0 w-100 h-50 pointer-events-none" style={{ background: "linear-gradient(0deg, rgba(2,6,23,1) 0%, transparent 100%)" }}></div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>

        <style jsx>{`
          .about-section {
            background-color: #020617; /* Slate 950 base */
            font-family: 'Inter', system-ui, sans-serif;
          }
          
          .bg-absolute {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            z-index: 0;
          }
          
          .bg-dark-base {
            background-image: 
              linear-gradient(rgba(255, 255, 255, 0.02) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255, 255, 255, 0.02) 1px, transparent 1px);
            background-size: 60px 60px;
            background-position: center center;
            opacity: 0.5;
            mask-image: radial-gradient(ellipse at top, black 30%, transparent 80%);
            -webkit-mask-image: radial-gradient(ellipse at top, black 30%, transparent 80%);
          }

          .ambient-glow {
            position: absolute;
            border-radius: 50%;
            filter: blur(120px);
            z-index: 0;
            opacity: 0.3;
            pointer-events: none;
            animation: float-glow 25s ease-in-out infinite alternate;
          }

          .glow-primary {
            width: 600px;
            height: 600px;
            background: rgba(99, 102, 241, 0.15); /* Indigo */
            top: 10%;
            right: -100px;
            animation-delay: -5s;
          }

          .glow-secondary {
            width: 500px;
            height: 500px;
            background: rgba(139, 92, 246, 0.15); /* Violet */
            bottom: -100px;
            left: -100px;
            animation-delay: -15s;
          }

          @keyframes float-glow {
            0% { transform: translate(0, 0) scale(1); }
            33% { transform: translate(-30px, 50px) scale(1.1); }
            66% { transform: translate(20px, -20px) scale(0.9); }
            100% { transform: translate(0, 0) scale(1); }
          }

          .glass-panel {
            background: rgba(15, 23, 42, 0.4);
            backdrop-filter: blur(16px);
            -webkit-backdrop-filter: blur(16px);
          }

          .bg-dark-inner {
            background: #020617;
          }

          .border-white-10 {
            border-color: rgba(255, 255, 255, 0.08) !important;
          }

          .text-secondary {
            color: #94a3b8 !important; /* Slate 400 */
          }
          
          .text-xs {
            font-size: 0.75rem;
          }

          .tracking-wider {
            letter-spacing: 0.05em;
          }

          .badge-premium {
            background: linear-gradient(180deg, rgba(30, 41, 59, 0.7) 0%, rgba(15, 23, 42, 0.7) 100%);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.1);
          }

          .badge-dot {
            width: 8px;
            height: 8px;
            background-color: #8b5cf6; /* Violet 500 */
            border-radius: 50%;
            box-shadow: 0 0 8px #8b5cf6;
            animation: pulse-dot 2s infinite;
          }

          @keyframes pulse-dot {
            0% { box-shadow: 0 0 0 0 rgba(139, 92, 246, 0.4); }
            70% { box-shadow: 0 0 0 6px rgba(139, 92, 246, 0); }
            100% { box-shadow: 0 0 0 0 rgba(139, 92, 246, 0); }
          }

          .title-glow {
            text-shadow: 0 0 40px rgba(255, 255, 255, 0.1);
          }

          .stat-item {
            transition: transform 0.3s ease;
          }
          
          .stat-item:hover {
            transform: translateX(5px);
          }

          .premium-card {
            transition: transform 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275), box-shadow 0.6s ease, border-color 0.6s ease;
          }
          
          .shadow-premium {
            box-shadow: 
              0 25px 50px -12px rgba(0, 0, 0, 0.7),
              0 0 0 1px rgba(255, 255, 255, 0.05);
          }

          .premium-card:hover {
            transform: translateY(-12px);
            border-color: rgba(255, 255, 255, 0.2) !important;
            box-shadow: 
              0 40px 80px -12px rgba(0, 0, 0, 0.9),
              0 0 0 1px rgba(255, 255, 255, 0.2),
              0 0 40px rgba(99, 102, 241, 0.15);
          }
          
          .image-wrapper img {
            transition: transform 0.7s cubic-bezier(0.22, 1, 0.36, 1);
          }
          
          .premium-card:hover .image-wrapper img {
            transform: scale(1.03);
          }

          .btn-premium-action {
            background: #ffffff;
            color: #020617;
            border: 1px solid transparent;
            box-shadow: 0 4px 14px 0 rgba(255, 255, 255, 0.25);
            text-decoration: none;
            letter-spacing: 0.02em;
          }

          .btn-premium-action:hover {
            background: #f8fafc;
            color: #020617;
            transform: translateY(-2px);
            box-shadow: 0 6px 20px rgba(255, 255, 255, 0.35);
          }

          .pointer-events-none {
            pointer-events: none;
          }
        `}</style>
      </section>
    </>
  );
};

export default AboutSection;